import {
  faCircle,
  faCircleCheck,
  faClock,
  faCreditCard,
  faFlag,
  faKey,
  faLayerGroup,
  faPlug,
  faReply,
  faStairs,
  faTriangleExclamation,
  faWrench
} from '@fortawesome/free-solid-svg-icons';

export const LANES = [
  { key: 'needs', label: 'New', color: '#1890FF', chip: 'info' },
  { key: 'progress', label: 'In progress', color: '#FAAD14', chip: 'warn' },
  { key: 'review', label: 'Needs review', color: '#722ED1', chip: 'purple' },
  { key: 'office', label: 'Escalations', color: '#13C2C2', chip: 'info' },
  { key: 'done', label: 'Completed', color: '#52C41A', chip: 'ok' }
];
export const OPEN_LANES = LANES.filter((l) => l.key !== 'done');
export const laneOf = (key) => LANES.find((l) => l.key === key) || LANES[0];

export const TYPES = [
  ['leasing', 'Leasing', faKey],
  ['maintenance', 'Maintenance', faWrench],
  ['emergency', 'Emergency', faTriangleExclamation],
  ['followup', 'Follow-up', faReply],
  ['billing', 'Billing', faCreditCard],
  ['system', 'Other', faCircle]
];
export const ALL_TYPES = ['all', 'All', faLayerGroup];
export const typeIcon = (key) => TYPES.find((t) => t[0] === key)?.[2] || faCircle;

export const OUTCOMES = {
  sales: [
    ['won', 'Won'],
    ['lost', 'Lost'],
    ['on_hold', 'On hold'],
    ['disqualified', 'Disqualified']
  ],
  service: [
    ['resolved', 'Resolved'],
    ['disqualified', 'Disqualified']
  ]
};
export const REASONS = [
  ['duplicate', 'Duplicate'],
  ['spam', 'Spam']
];
export const isSales = (x) => x?.type === 'leasing';
export const outcomeLabel = (key) => [...OUTCOMES.sales, ...OUTCOMES.service].find((o) => o[0] === key)?.[1] || '';

export const PRIORITIES = [
  ['none', 'None'],
  ['normal', 'Normal'],
  ['high', 'High'],
  ['urgent', 'Urgent']
];
export function prioOf(x) {
  if (x.priority && x.lane !== 'done') return x.priority === 'none' ? 'normal' : x.priority;
  if (x.type === 'emergency' && x.lane !== 'done') return 'urgent';
  if (x.pri === 'High') return 'high';
  return 'normal';
}
const PRIO_RANK = { urgent: 0, high: 1, normal: 2 };
export const byUrgency = (a, b) => PRIO_RANK[prioOf(a)] - PRIO_RANK[prioOf(b)] || new Date(a.arrivedAt) - new Date(b.arrivedAt);

export const ATTEMPTS = [
  ['no_answer', 'No answer'],
  ['left_vm', 'Left VM'],
  ['reached', 'Reached'],
  ['callback', 'Callback'],
  ['not_needed', 'Not needed']
];
export const typeLabel = (key) => TYPES.find((t) => t[0] === key)?.[1] || key;

export const TEAM_ROLES = [
  ['owner', 'Owner'],
  ['office_manager', 'Office manager'],
  ['leasing_agent', 'Leasing agent'],
  ['maintenance_coordinator', 'Maintenance coordinator']
];
export const WAITS = [...TEAM_ROLES, ['agent_zero', 'Agent Zero team']];

export const roleLabel = (key, agent = 'Assistant') =>
  key === 'agent' ? agent : key === 'me' ? 'Me' : WAITS.find((w) => w[0] === key)?.[1] || key || '';

export const roleDefault = (teamRole) => (['leasing_agent', 'maintenance_coordinator'].includes(teamRole) ? teamRole : null);

export const canReassignAny = (user) => user?.userType === 'platform_admin' || ['owner', 'manager'].includes(user?.role);

export const isMe = (x, me) => Boolean(x.mine?.userId) && String(x.mine.userId) === String(me?._id);

export const isMine = (x, me) => (x.mine?.userId ? isMe(x, me) : Boolean(me?.teamRole) && x.wait === me.teamRole);

export const matchWait = (x, waits, me) =>
  !waits?.length || x.lane === 'done' || waits.some((w) => (w === 'me' ? isMine(x, me) : x.wait === w));

export const defaultWaits = (teamRole) => (roleDefault(teamRole) ? ['me'] : []);

const fmtCache = new Map();
const fmt = (date, tz, options) => {
  const key = `${tz}|${JSON.stringify(options)}`;
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...options });
    fmtCache.set(key, f);
  }
  return f.format(new Date(date));
};
export const clock = (date, tz) => (date ? fmt(date, tz, { hour: 'numeric', minute: '2-digit' }) : '');
const dayKey = (date, tz) => fmt(date, tz, { year: 'numeric', month: '2-digit', day: '2-digit' });

export function tzAbbr(tz, date = new Date()) {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value;
  return /^[A-Z]{3}$/.test(name || '') ? name.replace(/[SD]T$/, 'T') : name || '';
}

export function stampOf(date, tz, now = new Date()) {
  if (!date) return '';
  return dayKey(date, tz) === dayKey(now, tz) ? clock(date, tz) : `${fmt(date, tz, { weekday: 'short' })} ${clock(date, tz)}`;
}

export const timeOf = (x, tz, now) =>
  stampOf(x.lane === 'done' ? x.resolvedAt || x.arrivedAt : x.lane === 'needs' && x.dueAt ? x.dueAt : x.arrivedAt, tz, now);

const span = (mins) => (mins >= 60 ? `${Math.floor(mins / 60)} h ${mins % 60} min` : `${mins} min`);

export function waiting(x, now = new Date()) {
  const mins = Math.floor((now - new Date(x.arrivedAt || x.createdAt)) / 60000);
  if (mins < 0 || x.lane === 'done') return null;
  return { text: `Waiting ${span(mins)}`, over: Boolean(x.window && mins > x.window) };
}

export function ladderLeft(x, now = new Date()) {
  const step = x.ladder?.steps?.[x.ladder.cur];
  if (!step?.when) return null;
  const left = Math.max(0, Math.round(((x.ladder.window || 60) * 60000 - (now - new Date(step.when))) / 1000));
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
}

export const liveLadder = (x) => x.lane === 'needs' && x.ladder && x.ladder.steps?.some((s) => s.state === 'Paged');

export function rank(x) {
  if (liveLadder(x)) return 0;
  if (x.aging?.state === 'red') return 1;
  if (x.aging?.state === 'amber') return 2;
  return 3;
}

export const PATH = {
  Corrected: ['ok', faCircleCheck, 'Corrected'],
  Flagged: ['warn', faFlag, 'Flagged: not right'],
  Escalating: ['bad', faStairs, 'Escalating'],
  Aging: ['warn', faClock, 'Aging'],
  Overdue: ['bad', faClock, 'Overdue'],
  System: ['purple', faPlug, 'System']
};

export const oneTapLabel = (x) => {
  const label = x.approve?.oneTap || (x.approve?.label || '').replace(/^Approve: /, '');
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
};

export const firstName = (name = '') => String(name).trim().split(/\s+/)[0] || 'there';
